import { useStepStore, useStoreActions } from "@store/index";
import { STEP_ID, type StepType } from "@/types/index";

/**
 * Custom hook for managing workflow navigation
 */
export const useWorkflow = () => {
  const {
    setCurrentStep,
    markStepCompleted,
    removeCompletedStep,
    setManualInput,
  } = useStoreActions();

  const { steps, currentStep, completedSteps, wasFromAccount } = useStepStore();

  const canGoBack = () => {
    return steps && steps.length > 1 && currentStep !== steps[0];
  };

  const canGoNext = () => {
    return currentStep !== steps?.[steps.length - 1];
  };

  const handleNext = () => {
    if (!canGoNext() || !currentStep) return;
    markStepCompleted(currentStep);
    if (steps) {
      const currentIndex = steps.indexOf(currentStep);
      if (currentIndex < steps.length - 1) {
        const nextStep = steps[currentIndex + 1];
        if (nextStep) {
          setCurrentStep(nextStep);
        }
      }
    }
  };

  const handleBack = () => {
    if (!canGoBack() || !currentStep) return;
    removeCompletedStep(currentStep);
    if (steps) {
      if (currentStep.id === STEP_ID.ADVANCED_PRESCRIPTION && wasFromAccount) {
        setManualInput(false);
      } else {
        const currentIndex = steps.indexOf(currentStep);
        if (currentIndex > 0) {
          const previousStep = steps[currentIndex - 1];
          if (previousStep) {
            setCurrentStep(previousStep);
          }
        }
      }
    }
  };

  const handleStepChange = (step: StepType) => {
    setCurrentStep(step);
  };

  const isStepCompleted = (step: StepType) => {
    return completedSteps.includes(step);
  };

  return {
    steps,
    currentStep,
    completedSteps,
    canGoBack: canGoBack(),
    canGoNext: canGoNext(),
    handleNext,
    handleBack,
    handleStepChange,
    isStepCompleted,
  };
};
